import React, { useEffect, useMemo, useRef, useState } from "react";
import { RowSelectionState } from "@tanstack/react-table";
import { PaginatedResponseWithCountDto } from "@/client/extend";
import { BulkServiceVersionSelectionTable } from "./bulk-service-version-selection-table";
import {
  BulkDeviceServiceVersionSelectionTableContext,
  BulkSiteServiceVersionSelectionTableContext,
} from "./bulk-service-version-selection-table-context";
import { ServiceVersionSelectionStateProvider } from "./service-version-selection-provider";
import { useServiceVersionSelectionState } from "./service-version-selection-state";
import { ServiceVersionDto } from "@/client";
import { DataTableButtons } from "@/components/custom/data-table/buttons/data-table-buttons";
import { usePermissions } from "@/hooks/use-permissions";
import { Button } from "../button";
import { useTooltipProps } from "../tooltip";
import { TbSelect } from "react-icons/tb";
import { useCurrentRef } from "@/hooks/use-current-ref";
import { ServiceName } from "./ServiceNameType";

interface SelectionObserverProps {
  onSelect: (value: { version: string; type?: string } | undefined) => void;
  context: any;
}

const SelectionObserver = ({ onSelect, context }: SelectionObserverProps) => {
  const [rowSelection] = context.useTableRowSelection();
  const queryResult = context.useTableQueryResult();
  const { typeByVersion, setTypeForVersion } =
    useServiceVersionSelectionState();
  const prevSelectionRef = useRef<string | null>(null);
  const [_, setPinnedRow] = context.useTablePinnedRow();
  const setPinnedRowRef = useCurrentRef(setPinnedRow);
  const prevTypeRef = useRef<string | undefined>(undefined);
  const onSelectRef = useCurrentRef(onSelect);

  const selectedId = useMemo(() => {
    const selectedIds = Object.keys(rowSelection).filter(
      (key) => (rowSelection as RowSelectionState)[key],
    );
    return selectedIds[0] ?? null;
  }, [rowSelection]);

  useEffect(() => {
    const data = queryResult.data as
      | PaginatedResponseWithCountDto<ServiceVersionDto>
      | undefined;
    const versions = data?.results ?? [];
    const selectedVersion = versions.find(
      (version) => version.version === selectedId,
    );

    if (!selectedId || !selectedVersion) {
      prevSelectionRef.current = null;
      prevTypeRef.current = undefined;
      return;
      // onSelectRef.current(undefined);
      // return;
    }

    const defaultType = selectedVersion.type ?? selectedVersion.types?.[0];
    const selectedType = typeByVersion[selectedId] ?? defaultType;

    if (!typeByVersion[selectedId] && selectedType) {
      setTypeForVersion(selectedId, selectedType);
    }

    if (
      selectedId !== prevSelectionRef.current ||
      selectedType !== prevTypeRef.current
    ) {
      prevSelectionRef.current = selectedId;
      prevTypeRef.current = selectedType;
      if (selectedVersion) {
        setPinnedRowRef.current(selectedVersion);
      }
      onSelectRef.current({ version: selectedId, type: selectedType });
    }
  }, [queryResult.data, selectedId, setTypeForVersion, typeByVersion, context]);

  return null;
};

function SearchAsVersionButton_({ onSelect, context }: SelectionObserverProps) {
  const {
    searchState: [search, setSearch],
  } = context.useTableSearch();
  const [, setRowSelection] = context.useTableRowSelection();
  const [pinnedRow, setPinnedRow] = context.useTablePinnedRow();
  const tooltipProps = useTooltipProps();
  if (!search || (pinnedRow?.version && pinnedRow.version === search))
    return undefined;
  return (
    <Button
      {...tooltipProps}
      data-tooltip-html={"Use as custom version"}
      onClick={(ev) => {
        const row = { version: search };
        setPinnedRow(row);
        setRowSelection({ [row.version]: true });
        onSelect(row);
      }}
    >
      <TbSelect />
    </Button>
  );
}

function SearchAsVersionButton(props: SelectionObserverProps) {
  const {
    roleState: [role],
  } = usePermissions();
  if (role !== "SuperAdmin") {
    return undefined;
  }
  return <SearchAsVersionButton_ {...props} />;
}

function Top(props: SelectionObserverProps) {
  const right = React.useCallback(
    () => SearchAsVersionButton(props),
    [props.onSelect, props.context],
  );
  return <DataTableButtons right={right} />;
}

interface BulkServiceVersionSelectionTableWithSelectionProps {
  onSelect: (value: { version: string; type?: string } | undefined) => void;
  componentName: ServiceName;
  resourceType: "device" | "site";
}

export const BulkServiceVersionSelectionTableWithSelection = ({
  onSelect,
  componentName,
  resourceType,
}: BulkServiceVersionSelectionTableWithSelectionProps) => {
  const context =
    resourceType === "device"
      ? BulkDeviceServiceVersionSelectionTableContext
      : BulkSiteServiceVersionSelectionTableContext;

  const queryArgumentState = useState({
    componentName,
    page: 1,
    limit: 10,
  });

  return (
    <ServiceVersionSelectionStateProvider>
      <context.Provider queryArgumentState={queryArgumentState as any}>
        <Top onSelect={onSelect} context={context} />
        <SelectionObserver onSelect={onSelect} context={context} />
        <BulkServiceVersionSelectionTable context={context} />
      </context.Provider>
    </ServiceVersionSelectionStateProvider>
  );
};
